import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import kioskApi, { IInstalledApp } from '~/apis/kiosk';
import { SettingsCard } from '~/components/module/setup/controls';
import { getErrorMessage } from '~/utils/helper/error';
import { canViewModule } from '~/utils/helper/permission';
import { useAppSelector } from '~/utils/redux/hooks';
import { selectPermission, selectUser } from '~/thunks/auth/authSlice';
import { setupRouteAbsolute } from '~/utils/routes/setup';

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((item) => b.includes(item));

// Which apps of the senior's phone stay available in Protect Mode.
const AllowedApps = () => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const permission = useAppSelector(selectPermission);
  const canUpdate = canViewModule(user, permission, 'seniorPhoneSetup');

  const [apps, setApps] = useState<IInstalledApp[]>([]);
  const [saved, setSaved] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = (await kioskApi.getAllowedApps()).data.data.apps;
      const allowed = list.filter((app) => app.isAllowed).map((app) => app.packageName);
      setApps(list);
      setSaved(allowed);
      setSelected(allowed);
    } catch {
      setFeedback({ type: 'error', message: t('pages.setup.loadFailed') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleApps = useMemo(() => {
    const query = search.trim().toLowerCase();
    return [...apps]
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .filter((app) => !query || app.displayName.toLowerCase().includes(query));
  }, [apps, search]);

  const isDirty = !sameSet(selected, saved);

  const toggle = (packageName: string, checked: boolean) => {
    setFeedback(undefined);
    setSelected((prev) => (checked ? [...prev, packageName] : prev.filter((item) => item !== packageName)));
  };

  const handleSync = async () => {
    setSyncing(true);
    setFeedback(undefined);
    try {
      await kioskApi.updateAllowedApps(selected);
      await load();
      setFeedback({ type: 'success', message: t('pages.setup.syncSuccess') });
    } catch (error) {
      const reason = getErrorMessage(error);
      setFeedback({
        type: 'error',
        message: reason ? `${t('pages.setup.syncFailed')} ${reason}` : t('pages.setup.syncFailed'),
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className='flex w-full flex-col gap-4'>
      <Link to={setupRouteAbsolute.config} className='w-fit text-sm text-muted-foreground underline hover:text-foreground'>
        &larr; {t('nav.setup.config')}
      </Link>

      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-xl font-semibold text-foreground'>{t('pages.setup.allowedApps.title')}</h1>
          <p className='text-sm text-muted-foreground'>{t('pages.setup.allowedApps.subtitle')}</p>
        </div>
        {canUpdate && (
          <button
            type='button'
            onClick={handleSync}
            disabled={!isDirty || syncing}
            className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {syncing ? t('pages.setup.syncing') : t('pages.setup.sync')}
          </button>
        )}
      </div>

      {/* the header spans the page; the content below stays a readable width */}
      <div className='mx-auto flex w-full max-w-3xl flex-col gap-4'>
        {feedback && (
          <p className={`text-sm ${feedback.type === 'success' ? 'text-foreground' : 'text-destructive'}`} role='status'>
            {feedback.message}
          </p>
        )}
        {canUpdate && isDirty && <p className='text-xs text-muted-foreground'>{t('pages.setup.unsynced')}</p>}
        {!canUpdate && <p className='text-xs text-muted-foreground'>{t('pages.setup.readOnly')}</p>}

        {loading && apps.length === 0 && <p className='text-sm text-muted-foreground'>{t('common.loading')}</p>}
        {!loading && apps.length === 0 && (
          <p className='text-sm text-muted-foreground'>{t('pages.setup.allowedApps.empty')}</p>
        )}

        {apps.length > 0 && (
          <>
            <input
              type='search'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('pages.setup.table.search')}
              aria-label={t('pages.setup.table.search')}
              className='w-full max-w-sm rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground'
            />
            <SettingsCard>
              {visibleApps.length === 0 && (
                <p className='text-sm text-muted-foreground'>{t('pages.setup.table.noResults')}</p>
              )}
              <ul className='flex flex-col divide-y divide-border'>
                {visibleApps.map((app) => (
                  <li key={app.packageName}>
                    <label className='flex cursor-pointer items-center gap-3 py-2 text-sm'>
                      <span className='h-8 w-8 shrink-0 overflow-hidden rounded-lg bg-muted'>
                        {app.iconUrl && <img src={app.iconUrl} alt='' className='h-full w-full object-cover' />}
                      </span>
                      <span className='min-w-0 flex-1 truncate text-foreground'>{app.displayName}</span>
                      <input
                        type='checkbox'
                        checked={selected.includes(app.packageName)}
                        disabled={!canUpdate}
                        onChange={(e) => toggle(app.packageName, e.target.checked)}
                      />
                    </label>
                  </li>
                ))}
              </ul>
            </SettingsCard>
          </>
        )}
      </div>
    </div>
  );
};

export default AllowedApps;
